import { useCallback, useEffect, useRef, useState } from 'react';
import type { GameType } from '@tv-casino/shared';

/** The switch a screen is currently announcing. */
export interface GameSplashCue {
  game: GameType;
  /** The felt this screen was showing before, or null if it had none. */
  from: GameType | null;
  /** Bumped per switch, so a re-entry restarts the card's animation. */
  key: number;
}

/**
 * Raise a switch card when the table changes game, and take it down again on
 * its own. Each screen runs its own clock off the published `gameType` rather
 * than off a server cue: the switch is already in every broadcast, so nothing
 * new has to travel, and a phone that joins mid-switch simply starts at the
 * new game instead of catching the tail of a card meant for the room.
 *
 * The first game a screen sees is not a switch — opening the TV on a blackjack
 * table is not a change of anything — so the card waits for a real move. A
 * dropped connection does not count either: `null` is ignored, so a reconnect
 * onto the same felt stays quiet.
 */
export function useGameSplash(
  game: GameType | null | undefined,
  ms: number,
): { cue: GameSplashCue | null; dismiss: () => void } {
  const seen = useRef<GameType | null>(null);
  const seq = useRef(0);
  const [cue, setCue] = useState<GameSplashCue | null>(null);

  useEffect(() => {
    if (!game) return;
    const was = seen.current;
    seen.current = game;
    if (was === null || was === game) return;
    if (ms <= 0) return;
    seq.current += 1;
    setCue({ game, from: was, key: seq.current });
  }, [game, ms]);

  // One timer per card, restarted by its key: a second switch inside the hold
  // replaces the card rather than inheriting the first one's remaining time.
  const showing = cue?.key ?? null;
  useEffect(() => {
    if (showing === null) return;
    const timer = setTimeout(() => {
      setCue((current) => (current?.key === showing ? null : current));
    }, ms);
    return () => clearTimeout(timer);
  }, [showing, ms]);

  const dismiss = useCallback(() => setCue(null), []);
  return { cue, dismiss };
}
